import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedDetails/components/CardSelectionSnackbar.style'
import {
  sidenavCollapsedWidth,
  sidenavExpandedWidth,
} from 'src/components/SidenavWrapper/sidenav.constants'
import SvgClose from 'src/components/Svgs/SvgClose'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'
import {
  selectIsSidenavLockedOpenFromState,
  selectShowSidenavLargeFromState,
} from 'src/redux/selectors/app.selectors'

export interface CardSelectionSnackbarProps {
  selectedCount: number
  onAssign: () => void
  onClose: () => void
}

const CardSelectionSnackbar: React.FC<CardSelectionSnackbarProps> = ({
  selectedCount,
  onAssign,
  onClose,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)
  const showSidenavLarge = useSelector(selectShowSidenavLargeFromState)

  if (selectedCount === 0) return null

  // The toolbar is position: fixed, so center it on the content area's midpoint
  // rather than the viewport's: shift right by half the sidenav width so it
  // doesn't sit biased to the left under the nav (EX-39827).
  //
  // Mirror the page container's own width math: when the large nav isn't shown
  // (compact/small-nav layouts), BackgroundPage/LayoutShell use the full width
  // and don't reserve any sidenav gutter — even though isSidenavLockedOpen can
  // still be true — so the toolbar must not shift either. Using a non-zero
  // width there would push it ~half the nav width off-center.
  const navWidth = showSidenavLarge
    ? sideNavLockedOpen
      ? sidenavExpandedWidth
      : sidenavCollapsedWidth
    : 0
  const left = `calc(50% + ${navWidth / 2}px)`

  const handleAssignClick = (): void => {
    analytics.track('card feed details', { action: 'assign all clicked' })
    onAssign()
  }

  return (
    <div
      role="region"
      aria-label={translate('Selected cards actions')}
      css={style.container}
      style={{ left }}
      data-testid="card-feed-cards-selection-snackbar"
    >
      {/*
        Live region so assistive tech announces the count as the user toggles
        selections (SC 4.1.3 — Status Messages). role="status" implies
        aria-live="polite" and aria-atomic="true"; declaring both explicitly
        documents intent for future readers.
      */}
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        css={style.count}
        data-testid="card-feed-cards-selection-count"
      >
        <PluralTrns
          singleSource={'1 card selected'}
          pluralSource={'${count} cards selected'}
          count={selectedCount}
        />
      </span>
      <span css={style.divider} aria-hidden="true" />
      <button
        type="button"
        css={style.assignButton}
        onClick={handleAssignClick}
        data-testid="card-feed-cards-selection-assign"
      >
        {translate('Assign Account Owner')}
      </button>
      <span css={style.divider} aria-hidden="true" />
      <button
        type="button"
        css={style.closeButton}
        onClick={onClose}
        aria-label={translate('Clear selection')}
        data-testid="card-feed-cards-selection-close"
      >
        <SvgClose
          width={20}
          height={20}
          viewBox="0 0 25 25"
          stroke="currentColor"
        />
      </button>
    </div>
  )
}

export default CardSelectionSnackbar
